<template>
    <ion-grid
        class="light-light-background purple-purple-color p-3 overflow-auto max-height-100 max-width"
        @touchmove="stopEventAtTop"
    >
        <ion-row>
            <ion-col class="d-flex" style="gap: 10px; flex-wrap: wrap;">
                <category-pill
                    class="light-light-color" v-for="tag of listStore.productTags(listId, listItemId)"
                    :key="tag.id" edit @edit="openEdit(tag)" @remove="removeTag(tag)"
                >
                    {{ tag.name }}
                </category-pill>
            </ion-col>
        </ion-row>

        <ion-row v-show="showEdit" class="center-y-children">
            <ion-col>
                <input-simple class="grey4-grey4-background purple-purple-color input-padding-left"
                    :placeholder="$t('placeholder.tagName')" v-model="tag.name" ref="tagNameInput" />
            </ion-col>

            <ion-col size="auto">
                <div class="ion-activatable round-ripple-parent p-1 square-box" @click="editTag">
                    <ion-icon :icon="checkmark" />
                    <ion-ripple-effect />
                </div>
            </ion-col>

            <ion-col size="auto">
                <div class="ion-activatable round-ripple-parent p-1 square-box" @click="resetInput">
                    <ion-icon :icon="close" />
                    <ion-ripple-effect />
                </div>
            </ion-col>
        </ion-row>

        <ion-row>
            <ion-col>
                <input-simple v-model="searchAdd"
                    class="grey4-grey4-background purple-purple-color card-border input-padding-left"
                    :placeholder="$t('placeholder.searchOrAdd')" @keydown.enter="addTag()" />
            </ion-col>
        </ion-row>

        <ion-row>
            <ion-col class="center">
                <button-base class="max-width" @click="addTag()" :disabled="!searchAdd">
                    {{ $t('button.create') }}
                </button-base>
            </ion-col>
        </ion-row>

        <ion-row class="mt-2 mb-4">
            <ion-col class="d-flex" style="gap: 10px; flex-wrap: wrap;">
                <category-pill class="light-light-color"
                    v-for="tag of listStore.filterRecommended(listStore.productTags(listId, listItemId), searchAdd)"
                    :key="tag.id" @click="addTag(tag)" @delete="deleteTag(tag)" light delete-able ripple>
                    {{ tag.name }}
                </category-pill>
            </ion-col>
        </ion-row>
    </ion-grid>
</template>

<script setup lang="ts">
import { IonCol, IonGrid, IonIcon, IonRippleEffect, IonRow } from "@ionic/vue";
import { nextTick, ref, computed } from "vue";
import router from "@/router";
import { t } from "@/plugins/vue-i18n";

import CategoryPill from "@/components/various/CategoryPill.vue";
import ButtonBase from "@/components/button/ButtonBase.vue";
import InputSimple from "@/components/input/InputSimple.vue";

import { useListStore } from "@/stores/lists";

import { replaceToast } from "@/functions/baseToast";
import { popAlertConfirm } from "@/functions/alert";
import { stopEventAtTop } from "@/functions/modal";

import { CategoryTag } from "@/types/supplier";
import { PartiallyRequired } from "@/types/helper";
import { TagDropdownProps } from "@/types/props";

import { checkmark, close } from "ionicons/icons";

const props = defineProps<TagDropdownProps>()
const emit = defineEmits<{ (e: 'update-item'): void }>()

const listStore = useListStore()

const showEdit = ref<boolean>(false)
const tag = ref<CategoryTag>(new CategoryTag(''))
const searchAdd = ref<string>('')
const tagNameInput = ref()
const isDisabled = ref<boolean>(false)

const locationId = computed<string>(() => String(router.currentRoute.value.params.locationId))

const openEdit = (editTag: CategoryTag): void => {
    tag.value = JSON.parse(JSON.stringify(editTag))

    showEdit.value = true

    nextTick(() => tagNameInput.value.$el.children[0].setFocus())
}

const resetInput = (): void => {
    tag.value.name = ''
    tag.value.id = ''
    showEdit.value = false
}

const deleteHandler = async (tagId: string): Promise<void> => {
    const response = await listStore.deleteTag(locationId.value, tagId)

    if (!response) return

    listStore.isLoaded.recommendedTags = false
    listStore.getTags(locationId.value)
    await listStore.getListItem(locationId.value, props.listId, props.listItemId)
}

const deleteTag = async (tag?: PartiallyRequired<CategoryTag, 'id'>): Promise<void> => {
    if (!tag) return

    await popAlertConfirm({
        message: t('alert.delete.tag', { target: tag.name }),
        handler: {
            ok: () => deleteHandler(tag.id)
        }
    })
}

const addTag = async (tag?: PartiallyRequired<CategoryTag, 'id'>): Promise<void> => {
    if ((!searchAdd.value && !tag) || isDisabled.value) return

    isDisabled.value = true
    listStore.isLoaded.listItems = false

    if (tag) {
        const response = await listStore.setTag({
            tagId: tag.id,
            locationId: locationId.value,
            listId: props.listId,
            listItemId: props.listItemId
        })

        listStore.isLoaded.listItems = true
        if (!response) return

        await replaceToast({
            message: t('success.add', { target: t('target.tag') }),
            id: 'add-tag'
        })
    } else {
        const response = await listStore.createTag(locationId.value, new CategoryTag(searchAdd.value))

        if (!response) {
            isDisabled.value = false
            listStore.isLoaded.listItems = true
            return
        }

        replaceToast({
            message: t('success.create', { target: t('target.tag') }),
            id: 'create-tag'
        })
        searchAdd.value = ''

        const responseSet = await listStore.setTag({
            tagId: response.id,
            locationId: locationId.value,
            listId: props.listId,
            listItemId: props.listItemId
        })

        if (!responseSet) {
            isDisabled.value = false
            listStore.isLoaded.listItems = true
            return
        }

        listStore.getListItem(locationId.value, props.listId, props.listItemId)
    }

    listStore.isLoaded.recommendedTags = false
    await listStore.getTags(locationId.value)
    await listStore.getListItem(locationId.value, props.listId, props.listItemId)
    isDisabled.value = false
    listStore.isLoaded.listItems = true
}

const editTag = async (): Promise<void> => {
    const response = await listStore.editTag(locationId.value, tag.value)

    if (!response) return

    replaceToast({
        message: t('success.edit', { target: t('target.tag') }),
        id: 'edit-tag'
    })

    listStore.isLoaded.recommendedTags = false

    resetInput()

    await listStore.getListItem(locationId.value, props.listId, props.listItemId)

    emit('update-item')
}

const removeTag = async (tag: PartiallyRequired<CategoryTag, 'id'>): Promise<void> => {
    const response = await listStore.unsetTag({
        tagId: tag.id,
        locationId: locationId.value,
        listId: props.listId,
        listItemId: props.listItemId
    })

    if (!response) {
        listStore.isLoaded.listItems = true
        return
    }

    await listStore.getListItem(locationId.value, props.listId, props.listItemId)
}
</script>

<style>
.input-padding-left>.native-input {
    padding-left: 10px;
}

.square-box {
    width: 2em;
    height: 2em;
}
</style>
